import React, { useState, useEffect } from "react";
import SeriesManager from "../components/SeriesManager"; // Use SeriesManager here
import { UserAuth } from "../context/AuthContext";
import { db } from "../firebase";
import {
  doc,
  collection,
  getDocs,
  setDoc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";
import CategoriesManager from "../components/CategoriesManager";
import { Trash2, UserRoundPlus } from "lucide-react";
import { Navigate } from "react-router-dom";

const Admin = () => {
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [isAccordionOpen, setIsAccordionOpen] = useState(false);

  const { user,isAdmin } = UserAuth();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) return;
      try {
        const adminDoc = await getDoc(doc(db, "admins", user.uid));
        setIsSuperAdmin(user.uid === "hvR4NCOFvsZWmribXsI8aFuk8vD3");
      } catch (error) {
        console.error("Error checking admin status:", error);
      }
    };
    checkAdminStatus();

    const fetchAdmins = async () => {
      try {
        const adminsCollection = await getDocs(collection(db, "admins"));
        const adminsList = adminsCollection.docs.map((doc) => ({
          uid: doc.id,
          email: doc.data().email,
        }));
        setAdmins(adminsList);
      } catch (error) {
        console.error("Error fetching admins:", error);
      }
    };
    fetchAdmins();
  }, [user]);

  const searchUsers = async () => {
    setLoading(true);
    try {
      const usersCollection = await getDocs(collection(db, "users"));
      const users = usersCollection.docs
        .map((doc) => ({ uid: doc.id, ...doc.data() }))
        .filter((user) =>
          user.email?.toLowerCase().includes(userSearch.toLowerCase())
        );
      setUserResults(users);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const addAdmin = async (uid, email) => {
    try {
      await setDoc(doc(db, "admins", uid), { email });
      alert(`User ${email} added as admin.`);
      setAdmins([...admins, { uid, email }]); // Update UI instantly
    } catch (error) {
      console.error("Error adding admin:", error);
      alert("Error adding admin:", error);
    }
  };

  const deleteAdmin = async (uid) => {
    if (!isSuperAdmin) {
      alert("You do not have permission to remove admins.");
      return;
    }
    try {
      await deleteDoc(doc(db, "admins", uid));
      setAdmins(admins.filter((admin) => admin.uid !== uid));
      alert("Admin removed successfully.");
    } catch (error) {
      console.error("Error deleting admin:", error);
    }
  };

  if (!isAdmin) {
    return <Navigate to="/forbidden" />;
  }

  return (
    <div className="flex flex-col lg:flex-row p-4 pt-36 min-h-screen bg-gray-900 text-white">
      {/* Left Column: User Search & Admin Management */}
      <div className="w-full lg:w-1/3 bg-gray-800 p-4 rounded-lg">
        <h2 className="text-xl font-bold mb-4">Manage Admins</h2>
        <input
          type="text"
          placeholder="Search users by email"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="w-full p-2 mb-4 bg-gray-700 rounded text-white"
        />
        <button
          onClick={searchUsers}
          className="flex items-center justify-center gap-2 w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Search
        </button>
        {loading && <p>Loading...</p>}
        {userResults.length === 0 && !loading && (
          <p className="text-gray-400">No users found.</p>
        )}
        {userResults.map((user) => (
          <div
            key={user.uid}
            className="flex justify-between items-center bg-gray-700 p-2 mb-2 rounded"
          >
            <p className="text-sm">{user.email}</p>
            <button
              onClick={() => addAdmin(user.uid, user.email)}
              className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded text-sm transition-colors"
            >
               <UserRoundPlus size={20} />
            </button>
          </div>
        ))}

        <button
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          className="w-full bg-gray-600 text-white p-2 rounded mt-4"
        >
          {isAccordionOpen ? "Hide Admins" : "Show Current Admins"}
        </button>

        {isAccordionOpen && (
          <div className="mt-4 max-h-48 overflow-y-auto">
            <h3 className="text-lg font-bold">Current Admins:</h3>
            {admins.length === 0 ? (
              <p className="text-gray-400">No admins found.</p>
            ) : (
              admins.map((admin) => (
                <div
                  key={admin.uid}
                  className="flex justify-between items-center bg-gray-700 p-2 mb-2 rounded"
                >
                  <p className="text-sm">{admin.email}</p>
                  {isSuperAdmin && (
                    <button
                      onClick={() => deleteAdmin(admin.uid)}
                      className="p-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      <Trash2 size={20} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Right Column: Series Manager */}
      <div className="w-full lg:w-2/3 flex flex-col space-y-4 p-4">
        {/* Display the SeriesManager to manage and view series */}
        <div className="bg-gray-800 p-4 rounded-lg">
          <h2 className="text-xl font-bold text-white mb-4">Manage Catagories</h2>
          <CategoriesManager /> {/* Render the SeriesManager here */}
        </div>
        <div className="bg-gray-800 p-4 rounded-lg">
          <h2 className="text-xl font-bold text-white mb-4">Manage Series</h2>
          <SeriesManager /> {/* Render the SeriesManager here */}
        </div>
      </div>
    </div>
  );
};

export default Admin;
